import FormErrorMessage from '@form/FormErrorMessage';
import StyledInput from '@ui/Form/StyledInput';
import { parseDate } from 'chrono-node';
import moment, { Moment } from 'moment';
import { ChangeEventHandler, useEffect, useState } from 'react';
import { useController, useFormContext } from 'react-hook-form';
import { DatePicker } from '~/components/Form/next/FormDatePicker';

interface Props {
    startName: string;
    endName: string;
    label?: string;
    withDate?: boolean;
    interval?: number;
    disabled?: boolean;
}

const TimeRange = ({ startName, endName, withDate, interval, disabled }: Props) => {
    const { control, getValues } = useFormContext();
    const [currentDate, setCurrenDate] = useState<Date>(moment(getValues(startName)).toDate());
    const { field: startField, fieldState: startFieldState } = useController({ control, name: startName });
    const { field: endField, fieldState: endFieldState } = useController({ control, name: endName });

    const parseTimeString = (value: string) => {
        const trimmed = value.trim();
        const match = trimmed.match(/^(\d{1,2})(?::(\d{1,2}))?$/);
        if (!match) return null;
        const hour = Number(match[1]);
        const minute = Number(match[2] ?? '0');
        if (!Number.isFinite(hour) || !Number.isFinite(minute)) return null;
        if (hour < 0 || hour > 23 || minute < 0 || minute > 59) return null;
        return { hour, minute };
    };

    const renderValues = (value: string | Date | null) => {
        if (value === null || value === undefined) return undefined;
        if (moment.isMoment(value)) return value.format('HH:mm');
        if (value instanceof Date) return moment(value).format('HH:mm');
        const parsedTime = parseTimeString(value);
        if (parsedTime) {
            return moment()
                .set({ hour: parsedTime.hour, minute: parsedTime.minute, second: 0, millisecond: 0 })
                .format('HH:mm');
        }
        const momentValue = moment(value);
        if (momentValue.isValid()) {
            return momentValue.format('HH:mm');
        }
        return moment(parseDate(value, { timezone: 'UTC' })).format('HH:mm');
    };

    useEffect(() => {
        setCurrenDate(moment(getValues(startName)).toDate());
    }, [startField.value]);

    const renderMoment = (value: string | Date | null, _currentDate?: Date): Moment => {
        const rendered = renderValues(value);
        if (!rendered) {
            return moment(_currentDate ?? currentDate);
        }
        const [hour, minute] = rendered.split(':').map(Number);
        return moment(_currentDate ?? currentDate).set({ hour, minute });
    };

    const handleChange: ChangeEventHandler<HTMLInputElement> = (e) => {
        const name = e.target.name;
        const value = e.target.value;
        const newDate = renderMoment(value);

        if (interval) {
            const diff = newDate.diff(renderMoment(startField.value), 'minutes');
            if (diff % interval !== 0) {
                newDate.add(interval - (diff % interval), 'minutes');
            }
        }

        switch (name) {
            case startName: {
                // move end time accordingly
                const endDate = renderMoment(endField.value);
                if (endDate && newDate.isAfter(endDate)) {
                    const diff = newDate.diff(endDate, 'minutes');
                    if (diff > 0) endField.onChange(newDate.clone().add(diff, 'minutes').toDate());
                }

                startField.onChange(newDate.toDate());
                break;
            }

            case endName: {
                // move start time accordingly
                const startDate = renderMoment(startField.value);
                if (startDate && newDate.isBefore(startDate) && newDate.isAfter(renderMoment('06:00'))) {
                    const diff = newDate.diff(startDate, 'minutes');
                    if (diff < 0) startField.onChange(newDate.clone().subtract(Math.abs(diff), 'minutes').toDate());
                }

                endField.onChange(newDate.toDate());
                break;
            }
        }
    };

    const handleDateUpdate = (dateString: string) => {
        const date = moment(dateString).toDate();
        setCurrenDate(date);
        startField.onChange(renderMoment(startField.value, date).toDate());
        endField.onChange(renderMoment(endField.value, date).toDate());
    };

    return (
        <div>
            {withDate && <DatePicker onChange={handleDateUpdate} value={currentDate} disabled={disabled} />}

            <div className="tw-flex tw-gap-4">
                <StyledInput
                    className="tw-flex-1"
                    isInvalid={startFieldState.invalid}
                    containerClassName="tw-flex-1"
                    type="time"
                    onBlur={startField.onBlur}
                    onChange={handleChange}
                    name={startField.name}
                    value={renderValues(startField.value)}
                    disabled={disabled}
                />
                <StyledInput
                    className="tw-flex-1"
                    isInvalid={endFieldState.invalid}
                    containerClassName="tw-flex-1"
                    type="time"
                    onBlur={endField.onBlur}
                    onChange={handleChange}
                    name={endField.name}
                    value={renderValues(endField.value)}
                    disabled={disabled}
                />
            </div>
            <FormErrorMessage name={startName} />
            <FormErrorMessage name={endName} />
        </div>
    );
};

export default TimeRange;
